// بیماران — the clinic's patient register, paged and searched server-side.
//
// Real paging from day one: the BFF returns { patients, total, limit, offset }
// and caps limit at 100. The doctor app hardcodes limit:100/offset:0 and throws
// `total_count` away, so its list silently stops at a hundred patients — a
// clinic of any size never sees the rest.
//
// Search is DB-side (name / phone / national id / file number) and, when the
// query is clinic-scoped, file-number matching is scoped too — so clinic A's
// «۱۲» can never surface clinic B's patient.

import React from 'react';
import { bffQuery, errorCode } from '../lib/bffClient.js';
import { DEFAULT_PAGE_SIZE, offsetFor } from '../lib/paging.js';

export const PATIENTS_QUERY = `
  query Patients($clinicId: ID, $search: String, $limit: Int, $offset: Int) {
    patients(clinicId: $clinicId, search: $search, limit: $limit, offset: $offset) {
      total
      limit
      offset
      patients {
        id
        name
        phone
        nationalId
        fileNumber
        gender
        age
        createdAt
        avatarUrl
      }
    }
  }
`;

/**
 * @param clinicId  the clinic to scope to. `null` means "do not ask yet" —
 *                  the caller has no membership to scope with, and an unscoped
 *                  call would be refused (or, on a service that ignored the
 *                  scope, would return the whole platform).
 */
export function usePatients({ clinicId, search, page = 1, pageSize = DEFAULT_PAGE_SIZE }) {
  const [patients, setPatients] = React.useState([]);
  const [total, setTotal] = React.useState(0);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);
  const [nonce, setNonce] = React.useState(0);

  React.useEffect(() => {
    let alive = true;

    if (!clinicId) {
      setPatients([]);
      setTotal(0);
      setLoading(false);
      setError(null);
      return () => {
        alive = false;
      };
    }

    setLoading(true);
    bffQuery(PATIENTS_QUERY, {
      clinicId,
      search: search || null,
      limit: pageSize,
      offset: offsetFor(page, pageSize),
    })
      .then((data) => {
        if (!alive) return;
        setPatients(data?.patients?.patients || []);
        setTotal(data?.patients?.total || 0);
        setError(null);
      })
      .catch((err) => {
        if (!alive) return;
        console.error('[usePatients] failed:', errorCode(err) || err?.message);
        setError(err);
        // A failed read must replace the list with an error state, never fall
        // through to an empty one: "no patients" and "we could not ask" are
        // different facts and only one of them means "register someone".
        setPatients([]);
        setTotal(0);
      })
      .finally(() => {
        if (alive) setLoading(false);
      });

    return () => {
      alive = false;
    };
  }, [clinicId, search, page, pageSize, nonce]);

  return { patients, total, loading, error, reload: () => setNonce((n) => n + 1) };
}

export default usePatients;
